import { useState } from 'react'

import {
  Alert,
  Box,
  Divider,
  Group,
  NumberInput,
  Stack,
  Text,
  Title,
} from '@mantine/core'
import {
  IconAlertCircle,
  IconBuildingBank,
  IconCheck,
} from '@tabler/icons-react'
import { useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'

import { usdFormatter } from '@repo/common/helpers/formatters'
import {
  useExternalFinancialAccount,
  useUpdateExternalFinancialAccount,
} from '@repo/common/queries/admin/external_financial'

import { TowerUserLink } from '@/components/TowerUserLink/TowerUserLink'
import { TowerApprovalControl } from '@/components/ui/TowerApprovalControl'
import { TowerCard } from '@/components/ui/TowerCard/TowerCard'
import { withTitle } from '@/utils/withTitle.ts'

export const Route = createFileRoute('/_app/external-financial-account/$id')({
  component: ExternalFinancialAccountPage,
  head: withTitle('External Financial Account'),
})

function ExternalFinancialAccountPage() {
  const { id } = Route.useParams()
  const queryClient = useQueryClient()

  const {
    data: account,
    isLoading,
    error,
  } = useExternalFinancialAccount({
    id: id!,
    refetchInterval: 30000, // Refresh every 30 seconds
  })

  const {
    mutateAsync: updateAccount,
    isPending,
    isError,
    error: mutationError,
  } = useUpdateExternalFinancialAccount()

  const [newBalance, setNewBalance] = useState<number | string>('')
  const [locked, setLocked] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  const handleUpdateBalance = async (): Promise<void> => {
    setFormError(null)
    setSuccessMessage(null)

    if (typeof newBalance !== 'number' || newBalance < 0) {
      setFormError('Balance must be a non-negative number')
      return
    }

    if (!account) {
      setFormError('Account not found')
      return
    }

    try {
      const result = await updateAccount({
        id: account.id,
        current_balance: newBalance,
        available_balance: newBalance, // Assuming available balance equals current balance
      })

      if (result.errors.length > 0) {
        setFormError(result.errors.join(', '))
        return
      }

      // Invalidate and refetch the account data
      await queryClient.invalidateQueries({
        queryKey: ['external_financial_account', id],
      })

      setSuccessMessage(
        `Balance updated successfully from ${usdFormatter(account.current_balance)} to ${usdFormatter(newBalance)}`,
      )
      setNewBalance('')
      setLocked(false)
    } catch (err) {
      setFormError(
        err instanceof Error ? err.message : 'Failed to update balance',
      )
    }
  }

  const handleStartApprove = (): void => {
    setLocked(true)
    setFormError(null)
    setSuccessMessage(null)
  }

  const handleCancelApprove = (): void => {
    setLocked(false)
    setFormError(null)
  }

  const isFormValid =
    typeof newBalance === 'number' &&
    newBalance >= 0 &&
    newBalance !== account?.current_balance

  if (isLoading) {
    return (
      <Stack>
        <Title order={2}>Loading External Financial Account...</Title>
      </Stack>
    )
  }

  if (error || !account) {
    return (
      <Stack>
        <Title order={2}>External Financial Account</Title>
        <Alert icon={<IconAlertCircle size={16} />} color="red">
          {error?.message || 'Account not found'}
        </Alert>
      </Stack>
    )
  }

  return (
    <Stack>
      <TowerCard
        title={account.name}
        description={`${account.custodian} • ${account.currency} Account`}
        icon={<IconBuildingBank size={20} />}
      >
        <Stack gap="lg">
          {/* Balance Display Section */}
          <Stack gap="sm">
            <Group justify="space-between">
              <Text size="lg" fw={600}>
                Current Balance
              </Text>
              <Text size="xl" fw={700} c="blue">
                {usdFormatter(account.current_balance)} {account.currency}
              </Text>
            </Group>

            <Group justify="space-between">
              <Text c="dimmed">Available Balance</Text>
              <Text fw={500}>
                {usdFormatter(account.available_balance)} {account.currency}
              </Text>
            </Group>

            <Group justify="space-between">
              <Text c="dimmed">Last Updated</Text>
              <Text fw={500}>
                {new Date(account.updated_at).toLocaleString()}
              </Text>
            </Group>

            <Group justify="space-between" align="flex-start">
              <Text c="dimmed">Updated By</Text>
              <TowerUserLink userId={account.updated_by} />
            </Group>
          </Stack>

          <Divider />

          {/* Balance Update Section */}
          <Stack gap="md">
            <Title fz="xl2">Update Balance</Title>

            <NumberInput
              label="New Balance"
              placeholder="Enter new balance amount"
              value={newBalance}
              onChange={setNewBalance}
              decimalScale={2}
              min={0}
              disabled={locked || isPending}
              error={
                typeof newBalance === 'number' && newBalance < 0
                  ? 'Balance cannot be negative'
                  : ''
              }
              description={
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                account.current_balance !== undefined && (
                  <Text size="sm" c="dimmed">
                    Current balance: {usdFormatter(account.current_balance)}{' '}
                    {account.currency}
                  </Text>
                )
              }
            />

            {successMessage && (
              <Alert icon={<IconCheck size={16} />} color="green">
                {successMessage}
              </Alert>
            )}

            {(formError || isError) && (
              <Alert icon={<IconAlertCircle size={16} />} color="red">
                {formError ||
                  (mutationError instanceof Error
                    ? mutationError.message
                    : 'An error occurred')}
              </Alert>
            )}

            <Box maw={400}>
              <TowerApprovalControl
                onApprove={handleUpdateBalance}
                onStartApprove={handleStartApprove}
                onCancel={handleCancelApprove}
                disabled={!isFormValid || isPending}
                buttonText="Update Balance"
                confirmationText="Slide to confirm balance update"
                successText="Balance updated successfully"
                mode="hold"
                holdDurationMs={1000}
              />
            </Box>
          </Stack>
        </Stack>
      </TowerCard>
    </Stack>
  )
}
